import Sentry from './common/utils/middleware/config.sentry.js'
import ReactDOM from 'react-dom/client'
import App from './App.jsx'
import './index.css'
// import { Provider } from 'react-redux'
// import { store } from '../src/common/redux/store/store.js'

import { ClarityProvider } from '@artprov/design-system'

/**
 * Normaliza el callback OIDC de Keycloak cuando llega en el hash de la URL.
 *
 * keycloak-js usa por defecto `responseMode: 'fragment'`, por lo que Keycloak
 * devuelve `code`/`state` en el fragmento (ej. `/public#code=...&state=...`).
 * El design-system detecta el callback sólo en el query string
 * (`hasOidcCallbackParams`), así que en una ruta PÚBLICA no lo reconoce, ejecuta
 * `checkKeycloakSession` (check-sso) y el callback nunca se procesa, provocando
 * un loop de redirección.
 *
 * Para evitarlo, si hay un callback en el hash y no estamos en la raíz, lo
 * reenviamos a `/` preservando el hash, de modo que `<AuthKeycloak>`
 * (login-required) lo procese. En `/` no se hace nada: `<AuthKeycloak>` ya lo
 * maneja.
 *
 * Como este reenvío descarta la ruta original (por ejemplo al recargar con F5
 * en `/cargar-nomina/2026-08/7821`), antes de redirigir guardamos esa ruta en
 * sessionStorage. `ProtectedLayout` la restaura una vez autenticado. Las rutas
 * públicas (`/public` y `/onboarding`) no se guardan: su login vuelve a `/` y no
 * deben reenviarse.
 */
const POST_LOGIN_REDIRECT_KEY = 'post-login-redirect'

/**
 * Rutas públicas que NO deben registrarse como destino post-login: si el
 * callback OIDC aterriza en una de ellas, el login debe regresar a `/` y no
 * reenviar al usuario a la pantalla pública.
 */
const PUBLIC_PATHS = ['/public', '/onboarding']

const normalizeKeycloakCallback = () => {
  const { hash, pathname, search, origin } = window.location
  const hasCallback = hash.includes('code=') && hash.includes('state=')
  if (!hasCallback || pathname === '/') return

  if (!PUBLIC_PATHS.includes(pathname)) {
    try {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, `${pathname}${search}`)
    } catch {
      // sessionStorage puede no estar disponible (modo privado); se ignora.
    }
  }

  window.location.replace(`${origin}/${hash}`)
}

normalizeKeycloakCallback()

// NOTA: no usar <React.StrictMode>. En desarrollo StrictMode ejecuta los efectos
// dos veces y esto interfiere con los guards a nivel módulo del design-system
// (`initializationPromise`/`checkSessionPromise`/`initInvoked`), provocando que
// la inicialización de Keycloak consuma el callback OIDC sin procesarlo y se
// entre en un loop de redirección. `tarifas_front` (que funciona) no lo usa.
const rootElement = document.getElementById('root')

if (!rootElement) {
  throw new Error('No se encontró el elemento root')
}

ReactDOM.createRoot(rootElement).render(
  <ClarityProvider projectId={import.meta.env.VITE_CLARITY_ID} sentry={Sentry}>
    <App />
  </ClarityProvider>
)
